
import { KursusoverviewModel } from "../models/KursusoverviewModel";
import { ColumnDef } from "@tanstack/react-table";
import useScreenResolution from "core/hooks/useScreenResolution";
import { nameof } from "ts-simple-nameof";
import { Localizer } from "infrastructure/localization/localizer";
import { UserProfileAvatarModel } from "core/components/userProfileAvatar/userProfileAvatarModel";
import { useMemo } from "react";
import { UserProfileAvatar, UserProfileAvatarSizeEnum } from "core/components/userProfileAvatar/userProfileAvatar";
import { GetUddannelsestypeTranslation, UddannelseTypeEnum } from "core/sharedmodels/stilling/uddannelseTypeEnum";
import { GetLaegekursusGruppeTranslation, LaegekursusGruppeTypeEnum } from "core/sharedmodels/kursus/laegekursusGruppeTypeEnum";
import { TableGodkendtCell } from "core/components/reactTable/tableCells/tableGodkendtCell";
import { ActionIcon } from "core/components/actionIcon/actionIcon";
import { RoutePaths } from "infrastructure/routes";

export const useKursusOverviewColumns = () => {
  const { isMobile } = useScreenResolution();
  const columns: ColumnDef<KursusoverviewModel>[] = useMemo(() => 
    [
      {
        accessorKey: nameof<KursusoverviewModel>(x => x.brugerNavn),
        id: nameof<KursusoverviewModel>(x => x.brugerNavn),
        header: Localizer.global_name(),
        cell: _ => "", //Dont show on subRows.
        aggregatedCell: ({ row }) => {
          const rowData = row.original;
          const userAvartarModel = new UserProfileAvatarModel(
            rowData.brugerId,
            rowData.brugerNavn,
            rowData.brugerInitialer,
            true,
            rowData.brugerEmail,
          );
          const brugerKursusRaekkeId = rowData.brugerKursusRaekkeId;

          return (
            <span className="flex-container-row flex-align-c">
              <UserProfileAvatar 
                avoidHoverTooltip={isMobile}
                userProfileAvatarModel={userAvartarModel}
                size={UserProfileAvatarSizeEnum.extraSmall}
              />
              <span className="margin-left-s">
                {userAvartarModel.Name} 
              </span>
              <ActionIcon addtionalClassNames="margin-left-s" iconName="arrow-right" routeUrl={RoutePaths.Kurser([userAvartarModel.UserId, brugerKursusRaekkeId]).url}/>
            </span>
          )
        },
        enableSorting: true,
        enableHiding: false,
      },
      {
        accessorKey: nameof<KursusoverviewModel>(x => x.uddannelseType),
        id: nameof<KursusoverviewModel>(x => x.uddannelseType),
        header: Localizer.global_uddannelse(),
        cell: info => info.row.original.isParentRow ? GetUddannelsestypeTranslation(info.getValue() as UddannelseTypeEnum) : "",
        aggregatedCell: info =>  GetUddannelsestypeTranslation(info.getValue() as UddannelseTypeEnum),
        enableSorting: true,
      },
      {
        accessorKey: nameof<KursusoverviewModel>(x => x.regions),
        id: nameof<KursusoverviewModel>(x => x.regions),
        header: Localizer.global_regioner(),
        cell: info => info.row.original.isParentRow ? info.getValue() : "",
        aggregatedCell: ({ row }) => {
          const regions = row.original.regions.map((region) => {
            return <div className="badge badge-pill badge-primary margin-right-xs">{region}</div>;
          })
          return regions;
        },
        enableSorting: false,
      },
      {
        accessorKey: nameof<KursusoverviewModel>(x => x.specialeNavn),
        id: nameof<KursusoverviewModel>(x => x.specialeNavn),
        header: Localizer.global_speciale(),
        cell: info => info.row.original.isParentRow ? info.getValue() : "",
        enableSorting: true,
      },
      {
        accessorKey: nameof<KursusoverviewModel>(x => x.kursusGruppe),
        id: nameof<KursusoverviewModel>(x => x.kursusGruppe),
        header: Localizer.global_kursusGruppe(),
        cell: info => info.getValue() ? GetLaegekursusGruppeTranslation(info.getValue() as LaegekursusGruppeTypeEnum) : null,
        aggregatedCell: ({ row }) => {
          const subRows = row.originalSubRows?.filter(x => !x.isPlaceholder);
          const kursusGrupper = subRows?.map(x => x.kursusGruppe).filterDistinct();
          return <span>{`(${kursusGrupper?.length} ${Localizer.global_kursusGrupper().toLowerCase()})`}</span>
        },
        enableSorting: false,
      },
      {
        accessorKey: nameof<KursusoverviewModel>(x => x.kursusTitel),
        id: nameof<KursusoverviewModel>(x => x.kursusTitel),
        header: Localizer.global_kursus(),
        cell: info => info.getValue() ?? null,
        aggregatedCell: ({ row }) => {
          const subRows = row.originalSubRows?.filter(x => !x.isPlaceholder);
          return <span>{`(${subRows?.length} ${Localizer.global_kurser().toLowerCase()})`}</span>
        },
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: nameof<KursusoverviewModel>(x => x.kursusStartDato),
        id: nameof<KursusoverviewModel>(x => x.kursusStartDato),
        header: Localizer.kursusoverview_kursusStart(),
        cell: info => {
          const dateValue = info.getValue() as Date | null;
          return dateValue ? dateValue.dateWithoutTimeFormat(true) : null;
        },
        enableSorting: false,
      },
      {
        accessorKey: nameof<KursusoverviewModel>(x => x.kursusSlutDato),
        id: nameof<KursusoverviewModel>(x => x.kursusSlutDato),
        header: Localizer.kursusoverview_kursusSlut(),
        cell: info => {
          const dateValue = info.getValue() as Date | null;
          return dateValue ? dateValue.dateWithoutTimeFormat(true) : null;
        },
        enableSorting: false,
      },
      {
        accessorKey: nameof<KursusoverviewModel>(x => x.slutDatoForSidsteAktiveDelForloeb),
        id: nameof<KursusoverviewModel>(x => x.slutDatoForSidsteAktiveDelForloeb),
        header: Localizer.kursusoverview_slutDatoForSidsteAktivDelForloeb(),
        cell: _ => "", //Dont show on subRows.
        aggregatedCell: ({ row }) => {
          const rawDate = row.original.slutDatoForSidsteAktiveDelForloeb;
          return <span>{rawDate?.dateWithoutTimeFormat(true)}</span>
        },
        enableSorting: false,
      },
      {
        accessorKey: nameof<KursusoverviewModel>(x => x.kursusGodkendtDato),
        id: nameof<KursusoverviewModel>(x => x.kursusGodkendtDato),
        header: Localizer.global_godkendt(),
        cell: info => <TableGodkendtCell godkendt={!!(info.getValue() as Date | undefined)} />,
        aggregatedCell: ({ row }) => {
          const subRows = row.originalSubRows?.filter(x => !x.isPlaceholder);
          const godkendteCount = subRows?.filter(x => !!x.kursusGodkendtDato).length ?? 0;
          const allKurserCount = subRows?.length ?? 0;
          const allKurserIsGodkendt = godkendteCount > 0 && godkendteCount === allKurserCount;
          return (
            <span className="flex-container-row flex-align-c">
              <TableGodkendtCell godkendt={allKurserIsGodkendt}  />
              <span className="margin-left-s">{`(${godkendteCount}/${allKurserCount})`}</span>
            </span>
          );
        },
        enableSorting: false,
      }
    ] as ColumnDef<KursusoverviewModel>[]
  , [isMobile]);

  return {
    columns
  }
}